import * as Yup from 'yup';
import Swal from 'sweetalert2';

const objectIdRegex = /^[a-f\d]{24}$/i;

export const defaultContractor = {
  company_name: '',
  category: '',
  description: '',
  company_id: '',
  representatives: [],
  properties: [],
};

const representativeSchema = Yup.object().shape({
  name: Yup.string().trim().nullable(),
  phone: Yup.string()
    .trim()
    .required('Phone number is required')
    .matches(/^\+?\d{7,15}$/, 'Phone must be 7–15 digits (optional + prefix)'),
  email: Yup.string()
    .trim()
    .transform((value) => (value === '' ? null : value))
    .nullable()
    .email('Invalid email format'),
});

const propertyAssignmentSchema = Yup.object().shape({
  property_id: Yup.string()
    .required('Property is required')
    .matches(objectIdRegex, 'Invalid Property ID format'),
  building_name: Yup.string()
    .trim()
    .required('Building name is required')
    .max(200, 'Building name is too long'),
});

export const addContractorSchema = Yup.object().shape({
  company_name: Yup.string()
    .trim()
    .required('Company name is required')
    .max(200, 'Company name is too long (max 200 characters)'),

  category: Yup.string()
    .trim()
    .required('Category is required')
    .max(100, 'Category name is too long'),

  description: Yup.string()
    .trim()
    .nullable()
    .max(1000, 'Description is too long (max 1000 characters)'),

  company_id: Yup.string()
    .nullable(),

  representatives: Yup.array()
    .of(representativeSchema)
    .min(1, 'At least one representative is required'),

  properties: Yup.array()
    .of(propertyAssignmentSchema)
    .test(
      'unique-property-building-combo',
      'The same property with the same building name cannot be assigned more than once',
      (assignments) => {
        if (!assignments || assignments.length === 0) return true;
        const seen = new Set();
        for (const a of assignments) {
          if (!a?.property_id || !a?.building_name) continue;
          const key = `${a.property_id}::${a.building_name.trim().toLowerCase()}`;
          if (seen.has(key)) {
            return false;
          }
          seen.add(key);
        }
        return true;
      }
    )
});

export const editContractorSchema = Yup.object().shape({
  company_name: Yup.string().trim().max(200),
  category: Yup.string().trim().max(100),
  description: Yup.string().trim().nullable().max(1000),
  company_id: Yup.string().nullable(),
  representatives: Yup.array()
    .of(representativeSchema)
    .min(1, 'At least one representative is required'),
  properties: Yup.array()
    .of(propertyAssignmentSchema)
    .test(
      'unique-property-building-combo',
      'The same property with the same building name cannot be assigned more than once',
      (assignments) => {
        if (!assignments || assignments.length === 0) return true;
        const seen = new Set();
        for (const a of assignments) {
          if (!a?.property_id || !a?.building_name) continue;
          const key = `${a.property_id}::${a.building_name.trim().toLowerCase()}`;
          if (seen.has(key)) {
            return false;
          }
          seen.add(key);
        }
        return true;
      }
    )
});

export const getValidationSchema = (mode) => {
  return mode === 'edit' ? editContractorSchema : addContractorSchema;
};

export const getContractorInitialValues = (mode, contractor = null, prefillProperty = null) => {
  if (mode === 'add' || !contractor) {
    const base = { ...defaultContractor };

    if (prefillProperty?.property_id) {
      base.properties = [
        {
          property_id: prefillProperty.property_id,
          building_name: prefillProperty.building_name || '',
        },
      ];
    }

    return base;
  }

  return {
    company_name: contractor.company_name || '',
    category: contractor.category || '',
    description: contractor.description || '',
    company_id: contractor.company_id || '',
    representatives: Array.isArray(contractor.representatives)
      ? contractor.representatives.map((rep) => ({
        name: rep.name || '',
        phone: rep.phone || '',
        email: rep.email || '',
      }))
      : [],
    properties: Array.isArray(contractor.properties)
      ? contractor.properties.map((p) => ({
        property_id: p.property_id || '',
        building_name: p.building_name || '',
      }))
      : [],
  };
};

const prettifyPath = (path) => {
  if (!path) return 'Field';

  const fieldMap = {
    company_name: 'Company Name',
    category: 'Category',
    description: 'Description',
    company_id: 'Company ID',
  };

  if (fieldMap[path]) return fieldMap[path];

  const repMatch = path.match(/^representatives\[(\d+)\]\.(.+)$/);
  if (repMatch) {
    const idx = Number(repMatch[1]) + 1;
    const field = repMatch[2];
    const fieldNames = {
      name: 'Name',
      phone: 'Phone',
      email: 'Email',
    };
    return `Representative #${idx} – ${fieldNames[field] || field}`;
  }

  const propMatch = path.match(/^properties\[(\d+)\]\.(.+)$/);
  if (propMatch) {
    const idx = Number(propMatch[1]) + 1;
    const field = propMatch[2];
    const fieldNames = {
      property_id: 'Property',
      building_name: 'Building Name',
    };
    return `Property Assignment #${idx} – ${fieldNames[field] || field}`;
  }

  return path
    .split('.')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).replace(/_/g, ' '))
    .join(' → ');
};

const groupErrors = (errors) => {
  const groups = {
    General: [],
    Representatives: [],
    'Property Assignments': [],
  };

  errors.forEach(({ path, message }) => {
    let cleanMsg = message;
    const label = prettifyPath(path);

    if (['company_name', 'category', 'description', 'company_id'].includes(path)) {
      groups.General.push({ label, message: cleanMsg });
    } else if (path.startsWith('representatives')) {
      groups.Representatives.push({ label, message: cleanMsg });
    } else if (path.startsWith('properties')) {
      groups['Property Assignments'].push({ label, message: cleanMsg });
    } else {
      groups.General.push({ label, message: cleanMsg });
    }
  });

  return groups;
};

export const validateContractor = async (values, isEdit = false) => {
  const schema = isEdit ? editContractorSchema : addContractorSchema;

  try {
    await schema.validate(values, { abortEarly: false });
    return true;
  } catch (err) {
    const errors = err.inner?.map((e) => ({
      path: e.path || '',
      message: e.message,
    })) || [];

    const grouped = groupErrors(errors);

    let html = `
      <div style="text-align: left; font-family: system-ui, -apple-system, sans-serif; max-width: 720px;">
        <p style="margin-bottom: 1.5rem; font-size: 1.1rem; color: #444;">
          Please correct the following issues:
        </p>
        <div style="background:#fffbeb; border:2px solid #fbbf24; border-radius:12px; padding:1.5rem; max-height:480px; overflow-y:auto;">
    `;

    const sectionIcons = {
      General: '🏢',
      Representatives: '👤',
      'Property Assignments': '🏠',
    };

    Object.entries(grouped).forEach(([section, items]) => {
      if (items.length > 0) {
        const icon = sectionIcons[section] || '⚠️';
        html += `
          <h4 style="margin:1.4rem 0 0.8rem; font-size:1.05rem; color:#d97706; border-bottom:1px solid #fcd34d; padding-bottom:0.4rem;">
            ${icon} ${section}
          </h4>
        `;
        items.forEach(({ label, message }, i) => {
          html += `
            <div style="margin:0.6rem 0; color:#444; padding-left:0.5rem; line-height:1.4;">
              <strong>${i + 1}. ${label}:</strong> ${message}
            </div>
          `;
        });
      }
    });

    html += `
        </div>
        <p style="margin-top:1.5rem; font-size:0.95rem; color:#555; text-align:center;">
          Fix the highlighted fields and try again.
        </p>
      </div>`;

    await Swal.fire({
      icon: 'warning',
      title: 'Form Issues Detected',
      html,
      width: 760,
      confirmButtonText: "Okay – I'll fix it",
      confirmButtonColor: '#f59e0b',
      background: '#fffef5',
      customClass: {
        popup: 'animated fadeIn',
      },
    });

    return false;
  }
};